import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api, setAuthToken, getAuthToken, clearAuthToken } from "../lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);

    const refresh = useCallback(async () => {
        if (!getAuthToken()) { setUser(null); setLoading(false); return null; }
        try {
            const { data } = await api.get("/auth/me");
            setUser(data);
            return data;
        } catch {
            clearAuthToken();
            setUser(null);
            return null;
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
        if (window.location.hash?.includes("session_id=")) {
            setLoading(false);
            return;
        }
        refresh();
    }, [refresh]);

    const _afterAuth = (data) => {
        if (data.access_token) setAuthToken(data.access_token);
        setUser(data);
        return data;
    };

    const login = async (email, password) => {
        const { data } = await api.post("/auth/login", { email, password });
        return _afterAuth(data);
    };

    const register = async (email, password, full_name) => {
        const { data } = await api.post("/auth/register", { email, password, full_name });
        return _afterAuth(data);
    };

    const logout = async () => {
        try { await api.post("/auth/logout"); } catch { /* ignore */ }
        clearAuthToken();
        setUser(null);
    };

    const facebookMockLogin = async () => {
        const { data } = await api.post("/auth/facebook/mock-login");
        return _afterAuth(data);
    };

    return (
        <AuthContext.Provider value={{ user, loading, login, register, logout, facebookMockLogin, refresh, setUser, setAuthToken }}>
            {children}
        </AuthContext.Provider>
    );
}

export const useAuth = () => useContext(AuthContext);
